<template>
  <div class="instruction-panel-entry">
    <!-- Title -->
    <h2 class="h2">
      <span v-if="assignedToMe">{{
        $t("instruction-panel.inscription-evaluation.title-assigned")
      }}</span>
      <span v-else>{{ $t("instruction-panel.inscription-evaluation.title") }}</span>
    </h2>

    <p v-if="!assignedToMe && projectIsInInstructionPhase" class="read-only-notice">
      {{ $t("instruction-panel.inscription-evaluation.unassigned-notice") }}
    </p>
    {{ $t("instruction-panel.instruction.instructeurs.text") }}
    <ul v-if="getInstructeurs()" class="instructeurs-list">
      <li v-for="ins in getInstructeurs()" :key="ins">{{ ins }}</li>
    </ul>
    <b-loading v-model="isLoading" is-full-page />
    <!-- Evaluation Result -->
    <p class="label">{{ $t("instruction-panel.inscription-evaluation.result-label") }}</p>

    <div class="evaluation-result-container">
      <div
        class="evaluation-result"
        :class="{
          active: mention == 'REFUSE' || isOverRefus,
          clickable: assignedToMe && projectIsInInstructionPhase,
        }"
        @mouseover="isOverRefus = assignedToMe && projectIsInInstructionPhase"
        @mouseout="isOverRefus = false"
        @click="changeMention('REFUSE')"
      >
        <img
          v-if="mention != 'REFUSE' && !isOverRefus"
          src="@/assets/images/icones/refused_inactive.png"
          :alt="$t('instruction-panel.inscription-evaluation.result-ko')"
        />
        <img
          v-else
          src="@/assets/images/icones/refused_active.png"
          :alt="$t('instruction-panel.inscription-evaluation.result-ko')"
        />
        <p
          :class="{
            'has-text-weight-bold': mention == 'REFUSE' || isOverRefus,
            'is-gray': mention != 'REFUSE' && !isOverRefus,
          }"
        >
          {{ $t("instruction-panel.inscription-evaluation.result-ko") }}
        </p>
      </div>

      <div
        class="evaluation-result"
        :class="{
          active: mention == 'ACCEPTE' || isOverAccept,
          clickable: assignedToMe && projectIsInInstructionPhase,
        }"
        @mouseover="isOverAccept = assignedToMe && projectIsInInstructionPhase"
        @mouseout="isOverAccept = false"
        @click="changeMention('ACCEPTE')"
      >
        <img
          v-if="mention != 'ACCEPTE' && !isOverAccept"
          src="@/assets/images/icones/accepted_inactive.png"
          :alt="$t('instruction-panel.inscription-evaluation.result-ok')"
        />
        <img
          v-else
          src="@/assets/images/icones/accepted_active.png"
          :alt="$t('instruction-panel.inscription-evaluation.result-ok')"
        />

        <p
          :class="{
            'has-text-weight-bold': mention == 'ACCEPTE' || isOverAccept,
            'is-gray': mention != 'ACCEPTE' && !isOverAccept,
          }"
        >
          {{ $t("instruction-panel.inscription-evaluation.result-ok") }}
        </p>
      </div>
    </div>

    <!-- Commentaire -->
    <InputWithValidation
      v-if="inscriptionProject.evaluationFinale"
      v-model="commentaireInstructeur"
      :validation-activated="false"
      type="textarea"
      :label="$t('instruction-panel.inscription-evaluation.commentaire-label')"
      :placeholder="$t('instruction-panel.inscription-evaluation.commentaire-notice')"
      maxlength="1400"
      :disabled="!assignedToMe"
      @manual-change="dirty = true"
    />

    <!-- Save -->
    <b-button
      v-if="assignedToMe && projectIsInInstructionPhase"
      class="aligned-right"
      type="is-primary"
      @click="sendResults"
    >
      {{ $t("projet.envoiResultats") }}
    </b-button>
    <b-button
      v-if="assignedToMe"
      class="aligned-right"
      type="is-primary"
      :disabled="!dirty"
      @click="save"
    >
      {{ $t("projet.save") }}
    </b-button>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import {
  ProjetAnnuelDTO,
  ProjetInscriptionDTO,
  StatutDossier,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { ProjectInstructionServiceV2 } from "@/services/rest/project/ProjectInstructionServiceV2"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { BButton, BLoading } from "buefy"

export default defineComponent({
  name: "InstructionPanelInscriptionEvaluation",
  components: {
    InputWithValidation,
    BButton,
    BLoading,
  },
  props: {
    project: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    loggedUser: {
      type: Object as PropType<UtilisateurDTO>,
      required: true,
    },
    instructionPanelWidth: {
      type: Number,
      required: true,
    },
  },

  emits: ["evaluation-saved"],
  data() {
    return {
      inscriptionProject: { ...this.project } as ProjetInscriptionDTO,
      projectInstructionService: ProjectInstructionServiceV2.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      assignedToMe: false,
      projectIsInInstructionPhase: false,
      dirty: false,
      isLoading: false,
      isOverAccept: false,
      isOverRefus: false,
      mention: undefined as StatutDossier | undefined,
    }
  },
  computed: {
    commentaireInstructeur: {
      get(): string {
        return this.inscriptionProject.evaluationFinale.commentaireInstructeur || ""
      },
      set(value: string) {
        if (this.inscriptionProject.evaluationFinale) {
          this.inscriptionProject.evaluationFinale.commentaireInstructeur = value
        }
      },
    },
  },
  watch: {
    project: {
      handler(newProject) {
        this.inscriptionProject = { ...newProject }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted(): void {
    this.inscriptionProject = this.inscriptionProject as ProjetInscriptionDTO
    this.projectIsInInstructionPhase =
      this.inscriptionProject.statut == "EN_ATTENTE" ||
      this.inscriptionProject.statut == "INSTRUCTION_EN_COURS"

    if (!this.loggedUser) {
      return
    }
    this.assignedToMe =
      (this.inscriptionProject.evaluationFinale &&
        this.inscriptionProject.evaluationFinale.instructeur &&
        this.inscriptionProject.evaluationFinale.instructeur.id === this.loggedUser.id) ||
      (this.inscriptionProject.autresInstructeursInscription &&
        this.inscriptionProject.autresInstructeursInscription.length !== 0 &&
        this.inscriptionProject.autresInstructeursInscription.some(
          (instructeur) => instructeur.id === this.loggedUser.id,
        ))

    if (this.inscriptionProject.evaluationFinale) {
      this.mention = this.inscriptionProject.evaluationFinale.mention
    }
  },
  methods: {
    changeMention(newStatut: StatutDossier): void {
      if (this.assignedToMe && this.projectIsInInstructionPhase) {
        this.mention = newStatut
        this.dirty = true
      }
    },

    async sendResults(): Promise<void> {
      if (!this.mention) {
        this.infoReportingService.error(
          this.$t("instruction-panel.inscription-evaluation.mention-error").toString(),
          new Error(),
        )
        return
      }

      // Créer une copie mise à jour pour l'envoi
      const updatedProject = {
        ...this.inscriptionProject,
        statut: this.mention as StatutDossier,
      } as ProjetInscriptionDTO

      this.infoReportingService.dialogWithCancel(
        this.$t("instruction-panel.inscription-evaluation.confirm-dialog.title").toString(),
        this.$t("instruction-panel.inscription-evaluation.confirm-dialog.message", [
          this.mention == "ACCEPTE" ? "accepter" : "refuser",
        ]).toString(),
        this.$t("instruction-panel.inscription-evaluation.confirm-dialog.confirm").toString(),
        () => this.doSave(updatedProject),
        () => {
          // Ne pas muter la prop, émettre l'état original
          this.$emit("evaluation-saved", { ...this.inscriptionProject, statut: "EN_ATTENTE" })
        },
        "is-info",
      )
    },

    async doSave(updatedProject?: ProjetInscriptionDTO): Promise<void> {
      this.isLoading = true
      const projectToSave = updatedProject || this.inscriptionProject
      await this.projectInstructionService.acceptOrDeclineProjectInscription(
        projectToSave as ProjetInscriptionDTO,
      )

      // Émettre la mise à jour vers le parent
      this.$emit("evaluation-saved", projectToSave)

      this.infoReportingService.success(
        projectToSave.statut == "ACCEPTE"
          ? this.$t("instruction-panel.inscription-evaluation.result-ok").toString()
          : this.$t("instruction-panel.inscription-evaluation.result-ko").toString(),
      )
      this.isLoading = false
      this.projectIsInInstructionPhase = projectToSave.statut == "EN_ATTENTE"
    },

    async save(): Promise<void> {
      if (this.mention && this.inscriptionProject.evaluationFinale) {
        this.inscriptionProject.evaluationFinale.mention = this.mention as StatutDossier
      }

      await this.projectInstructionService.updateInstructionInternalComment(
        this.inscriptionProject as ProjetInscriptionDTO,
      )

      // Émettre la mise à jour vers le parent
      this.$emit("evaluation-saved", this.inscriptionProject)

      this.infoReportingService.success(
        this.$t("instruction-panel.inscription-evaluation.comment-sent").toString(),
      )
      this.isLoading = false
      this.projectIsInInstructionPhase =
        this.inscriptionProject.statut == "EN_ATTENTE" ||
        this.inscriptionProject.statut == "INSTRUCTION_EN_COURS"
      this.dirty = false
    },

    getInstructeurs(): string[] {
      let instructeurs: UtilisateurDTO[] = []
      if (
        this.inscriptionProject.autresInstructeursInscription &&
        this.inscriptionProject.autresInstructeursInscription.length !== 0
      ) {
        instructeurs = [...this.inscriptionProject.autresInstructeursInscription]
      }
      if (
        this.inscriptionProject.evaluationFinale &&
        this.inscriptionProject.evaluationFinale.instructeur
      ) {
        instructeurs.push(this.inscriptionProject.evaluationFinale.instructeur)
      }

      if (instructeurs) {
        if (instructeurs.length !== 0) {
          return instructeurs.map(
            (instructeur) => instructeur.prenom + " " + instructeur.nom.toUpperCase(),
          )
        }
      }
      return [""]
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.label {
  color: #363636;
  font-size: 1rem;
  font-weight: 700;
  padding-top: 10px;
}

.read-only-notice {
  font-style: italic;
}

.evaluation-result-container {
  display: flex;
  gap: 10px;

  .evaluation-result {
    background-color: $instruction-result-background;
    border: 2px solid $bookmarkcard-border-color;
    border-radius: 10px;
    min-width: 100px;
    flex-grow: 1;
    display: flex;
    max-width: 200px;
    flex-direction: column;
    align-items: center;
    padding: 10px;
    cursor: not-allowed;

    &.active {
      background-color: $instruction-result-background-hover;
    }

    .is-gray {
      color: $placeholder-default-color;
    }

    img {
      width: 70%;
      max-width: 100px;
    }

    &.clickable {
      cursor: pointer;
    }
  }
}
</style>
